import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import apiRequest from '../utils/api';
import Modal from '../components/Modal';
import OrderApiIdsCell from '../components/OrderApiIdsCell';

// Config state for progress service IDs (loaded from backend)
let cachedProgressServiceIds: number[] | null = null;

interface Order {
  idSmm: number;
  user: string;
  idApi: string;
  provider: string;
  serviceId: number;
  link: string;
  uid?: string;
  quantity: number;
  start?: number;
  run: number;
  remains: number;
  comment?: string;
  accBuff?: string;
  note?: string;
  status: string;
  isReel: boolean;
  isGroup: boolean;
  isCancel: boolean;
  isRefill: boolean;
  cancelRequested: boolean;
  priceSystem: number;
  priceApi: number;
  totalPriceSystem: number;
  totalPriceApi: number;
  profitOrder: number;
  created: string;
}

const REFILL_CHECK_SERVICE_IDS: number[] = [
  1532, 1533, 1498, 3, 4, 26, 27, 28, 192, 1449, 190,
  5, 6, 7, 8, 9, 10, 12, 13,
  217, 218, 219, 220, 221, 222, 223, 224
];

const BULK_CANCEL_MAX_ORDERS = 1000;

const Orders: React.FC = () => {
  // Add CSS animation for bulk actions bar
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { hasAnyPermission, hasPermission } = useAuth();

  // Column visibility permissions (admin always has access via hasPermission)
  const canViewIdApi = hasPermission('orders_columns', 'view_id_api');
  const canViewProvider = hasPermission('orders_columns', 'view_provider');
  const canViewProfit = hasPermission('orders_columns', 'view_profit');
  const canViewPriceApi = hasPermission('orders_columns', 'view_price_api');

  // Progress service IDs from config
  const [progressServiceIds, setProgressServiceIds] = useState<number[]>([1532, 1533]);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all'); // Field to search in
  const [statusFilter, setStatusFilter] = useState('');
  const [stuckOrdersFilter, setStuckOrdersFilter] = useState(''); // Filter for stuck inprogress orders
  const [zeroRunFilter, setZeroRunFilter] = useState(false); // Filter for orders with run = 0
  const [idApiCountFilter, setIdApiCountFilter] = useState('');
  const [lossCancelFilter, setLossCancelFilter] = useState(false); // Filter for loss-cancelled orders
  const [providerServiceFilter, setProviderServiceFilter] = useState(''); // Filter by provider name or service ID
  const [showAdvancedEdit, setShowAdvancedEdit] = useState(false);

  // Not found orders tracking
  const [notFoundOrders, setNotFoundOrders] = useState<string[]>([]);
  const [totalSearchedIds, setTotalSearchedIds] = useState(0);
  const [totalFoundIds, setTotalFoundIds] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [aggregates, setAggregates] = useState({ totalQuantity: 0, totalPrice: 0, totalRemainingPrice: 0 });

  // Bulk selection state
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Format selection modal state
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [pendingCopyData, setPendingCopyData] = useState<{
    ids: string[];
    type: 'id_smm' | 'id_api' | 'uid';
  } | null>(null);

  // Expanded cells state for Link and UID columns
  const [expandedLinks, setExpandedLinks] = useState<Set<number>>(new Set());
  const [expandedUids, setExpandedUids] = useState<Set<number>>(new Set());

  // Action dropdown state
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);

  // Toggle expanded state for Link
  const toggleExpandLink = (orderId: number) => {
    setExpandedLinks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Toggle expanded state for UID
  const toggleExpandUid = (orderId: number) => {
    setExpandedUids(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Truncate text helper
  const truncateText = (text: string, maxLength: number = 25): string => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Bulk status update modal state
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('pending');
  const [bulkUpdateWebsite, setBulkUpdateWebsite] = useState(false);

  // Bulk update provider modal state
  const [showBulkProviderModal, setShowBulkProviderModal] = useState(false);
  const [bulkProviderName, setBulkProviderName] = useState('');

  // Single order update website state
  const [singleUpdateWebsite, setSingleUpdateWebsite] = useState(false);

  // Single order update confirmation modal (reserved for future use)
  // const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);
  // const [pendingUpdateData, setPendingUpdateData] = useState<any>(null);

  // Bulk cancel states
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);
  const [bulkCancelLoading, setBulkCancelLoading] = useState(false);
  const [isLossCancel, setIsLossCancel] = useState(false);

  // Bulk refill states
  const [showBulkRefillModal, setShowBulkRefillModal] = useState(false);
  const [bulkRefillLoading, setBulkRefillLoading] = useState(false);

  // Check provider status states
  const [showCheckStatusModal, setShowCheckStatusModal] = useState(false);
  const [checkStatusLoading, setCheckStatusLoading] = useState(false);
  const [checkStatusDesired, setCheckStatusDesired] = useState<'completed' | 'refunded'>('completed');
  const [checkStatusUpdateDb, setCheckStatusUpdateDb] = useState(false);
  const [checkStatusResult, setCheckStatusResult] = useState<{
    valid: Array<{ idSmm: number; idApi: string; status: string; refund: number; currentDbStatus: string }>;
    invalid: Array<{ idSmm: number; idApi: string; status: string; refund: number; currentDbStatus: string }>;
    errors: Array<{ idSmm: number; idApi: string; error: string }>;
    notFound: number[];
    wrongProvider: number[];
    updatedToInprogress: number;
    summary: { total: number; valid: number; invalid: number; errors: number; notFound: number; wrongProvider: number };
  } | null>(null);

  // Progress order modal state (for services configured in PROGRESS_SERVICE_IDS)
  interface ProgressOrderItem {
    id: number;
    idSmm: string;
    idApi: string | null;
    quantity: number;
    start: number | null;
    run: number;
    countSent: number;
    status: string;
    created: string;
  }
  const [progressModal, setProgressModal] = useState<{
    open: boolean;
    idSmm: string;
    data: ProgressOrderItem[];
    loading: boolean;
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  }>({ open: false, idSmm: '', data: [], loading: false, page: 1, limit: 20, total: 0, totalPages: 0 });

  // Function to fetch progress orders with pagination
  const fetchProgressOrders = async (idSmm: string, page: number = 1) => {
    setProgressModal(prev => ({ ...prev, loading: true }));
    try {
      const resp = await apiRequest(`/api/orders/progress/${idSmm}?page=${page}&limit=20`);
      const json = await resp.json();
      setProgressModal(prev => ({
        ...prev,
        data: json.data || [],
        total: json.total || 0,
        page: json.page || 1,
        totalPages: json.totalPages || 0,
        loading: false
      }));
    } catch {
      setProgressModal(prev => ({ ...prev, loading: false }));
      showNotification('error', 'Không thể tải dữ liệu progress');
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    idSmm: '',
    user: '',
    idApi: '',
    provider: '',
    serviceId: '',
    link: '',
    uid: '',
    quantity: '',
    comment: '',
    status: 'pending',
    priceSystem: '',
    priceApi: '',
    // Advanced fields
    start: '',
    run: '',
    remains: '',
    accBuff: '',
    note: '',
    isReel: false,
    isGroup: false,
    isCancel: false,
    isRefill: false
  });

  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>(
    { open: false, title: '', message: '', onConfirm: () => {} }
  );

  // View check result modal state
  const [viewCheckModal, setViewCheckModal] = useState<{
    open: boolean;
    type: 'success' | 'error' | 'info';
    title: string;
    message: string;
    details?: {
      currentViews?: number;
      initialViews?: number;
      purchasedQuantity?: number;
      minimumExpected?: number;
      orderId?: number;
    };
  }>({ open: false, type: 'info', title: '', message: '' });

  const fetchOrders = useCallback(async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }
      setError('');

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });

      if (searchTerm) {
        params.append('search', searchTerm);
        if (searchField !== 'all') {
          params.append('searchField', searchField);
        }
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      if (stuckOrdersFilter) {
        params.append('stuckOrders', stuckOrdersFilter);
      }

      if (zeroRunFilter) {
        params.append('zeroRun', 'true');
      }

      if (idApiCountFilter) {
        params.append('idApiCount', idApiCountFilter);
      }

      if (lossCancelFilter) {
        params.append('lossCancel', 'true');
      }

      if (providerServiceFilter.trim()) {
        params.append('providerService', providerServiceFilter.trim());
      }

      const response = await apiRequest(`/api/orders?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setOrders(data.data || []);
        setTotalItems(data.pagination?.total || 0);
        setIncompleteCount(data.incompleteCount || 0);
        setAggregates(data.aggregates || { totalQuantity: 0, totalPrice: 0, totalRemainingPrice: 0 });
        resetSelection(); // Reset selection when orders change

        // Check for not found orders when searching with comma-separated IDs
        if (searchTerm && searchField === 'idSmm') {
          const searchedIds = searchTerm.split(',').map(id => id.trim()).filter(id => id);

          if (searchedIds.length > 1) {
            // Multi-ID search: call separate API to check which IDs exist
            try {
              const checkResponse = await apiRequest('/api/orders/check-ids', {
                method: 'POST',
                body: JSON.stringify({ ids: searchedIds })
              });

              if (checkResponse.ok) {
                const checkData = await checkResponse.json();
                setNotFoundOrders(checkData.notFoundIds || []);
                setTotalSearchedIds(checkData.total || 0);
                setTotalFoundIds(checkData.found || 0);
              } else {
                setNotFoundOrders([]);
                setTotalSearchedIds(0);
                setTotalFoundIds(0);
              }
            } catch (checkError) {
              console.error('Failed to check IDs:', checkError);
              setNotFoundOrders([]);
              setTotalSearchedIds(0);
              setTotalFoundIds(0);
            }
          } else {
            // Single ID search: no need to check
            setNotFoundOrders([]);
            setTotalSearchedIds(0);
            setTotalFoundIds(0);
          }
        } else {
          setNotFoundOrders([]);
          setTotalSearchedIds(0);
          setTotalFoundIds(0);
        }
      } else {
        setError('Không thể tải danh sách đơn hàng');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
      console.error('Orders error:', error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setPageLoading(false);
      }
    }
  }, [currentPage, itemsPerPage, searchTerm, searchField, statusFilter, stuckOrdersFilter, zeroRunFilter, idApiCountFilter, lossCancelFilter, providerServiceFilter]);

  useEffect(() => {
    fetchOrders(true);

    // Fetch config for progress service IDs
    const fetchConfig = async () => {
      try {
        if (cachedProgressServiceIds) {
          setProgressServiceIds(cachedProgressServiceIds);
          return;
        }
        const resp = await apiRequest('/api/config');
        if (resp.ok) {
          const config = await resp.json();
          if (config.progressServiceIds && Array.isArray(config.progressServiceIds)) {
            cachedProgressServiceIds = config.progressServiceIds;
            setProgressServiceIds(config.progressServiceIds);
          }
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
      }
    };
    fetchConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate effect for pagination and search changes (not initial load)
  useEffect(() => {
    if (!loading) { // Only fetch if not initial load
      // Let the pagination effect fetch page 1 after a filter changes on a later page.
      if (currentPage !== 1) setCurrentPage(1);
      else fetchOrders(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchField, statusFilter, stuckOrdersFilter, zeroRunFilter, idApiCountFilter, lossCancelFilter, providerServiceFilter]);

  // Effect for pagination changes
  useEffect(() => {
    if (!loading) { // Only fetch if not initial load
      fetchOrders(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = editingOrder
        ? `/api/orders/${editingOrder.idSmm}`
        : '/api/orders';

      const method = editingOrder ? 'PUT' : 'POST';

      const basePayload: any = {
        ...formData,
        idSmm: parseInt(formData.idSmm),
        serviceId: parseInt(formData.serviceId),
        quantity: parseInt(formData.quantity),
        priceSystem: parseFloat(formData.priceSystem),
        priceApi: parseFloat(formData.priceApi),
        status: formData.status
      };

      // Normalize optional strings - convert empty to null
      if (basePayload.note === '') basePayload.note = null;
      if (basePayload.comment === '') basePayload.comment = null;
      if (basePayload.idApi === '') basePayload.idApi = null;
      if (basePayload.uid === '') basePayload.uid = null;
      if (basePayload.accBuff === '') basePayload.accBuff = null;

      if (showAdvancedEdit) {
        // Convert empty strings to null for numeric fields
        basePayload.start = formData.start === '' ? null : parseInt(formData.start);
        basePayload.run = formData.run === '' ? null : parseInt(formData.run);
        basePayload.remains = formData.remains === '' ? null : parseInt(formData.remains);
        if (formData.note) basePayload.note = formData.note;
        basePayload.isReel = !!formData.isReel;
        basePayload.isGroup = !!formData.isGroup;
        basePayload.isCancel = !!formData.isCancel;
        basePayload.isRefill = !!formData.isRefill;
      } else if (editingOrder) {
        if (typeof editingOrder.start === 'number') basePayload.start = editingOrder.start;
        if (typeof editingOrder.run === 'number') basePayload.run = editingOrder.run;
        if (typeof editingOrder.remains === 'number' && editingOrder.remains >= 0) {
          basePayload.remains = editingOrder.remains;
        } else {
          // Avoid sending invalid remains (e.g., -999) that fails backend validation
          delete basePayload.remains;
        }
        if (typeof editingOrder.note === 'string' && editingOrder.note.length > 0) {
          basePayload.note = editingOrder.note;
        }
        basePayload.isReel = editingOrder.isReel;
        basePayload.isGroup = editingOrder.isGroup;
        basePayload.isCancel = editingOrder.isCancel;
        basePayload.isRefill = editingOrder.isRefill;
        // For fields hidden to users without column view permissions, preserve existing values
        if (!canViewProvider) {
          basePayload.provider = editingOrder.provider;
        }
        if (!canViewPriceApi) {
          basePayload.priceApi = editingOrder.priceApi;
        }
      }

      // Add updateWebsite flag if editing and status changed
      if (editingOrder && singleUpdateWebsite && basePayload.status) {
        basePayload.updateWebsite = true;
      }

      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(basePayload)
      });

      if (response.ok) {
        const data = await response.json();
        await fetchOrders();
        resetForm();
        setSingleUpdateWebsite(false); // Reset checkbox

        // Show appropriate message
        const message = data.message || (editingOrder ? 'Cập nhật đơn hàng thành công!' : 'Tạo đơn hàng thành công!');
        showNotification('success', message);
      } else {
        const data = await response.json();
        showNotification('error', data.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    }
  };

  const handleEdit = (order: Order) => {
    setEditingOrder(order);
    setFormData({
      idSmm: order.idSmm.toString(),
      user: order.user,
      idApi: order.idApi || '',
      provider: order.provider,
      serviceId: order.serviceId.toString(),
      link: order.link,
      uid: order.uid || '',
      quantity: order.quantity.toString(),
      comment: order.comment || '',
      status: order.status,
      priceSystem: order.priceSystem.toString(),
      priceApi: order.priceApi.toString(),
      // Advanced fields - keep empty if null
      start: order.start?.toString() || '',
      run: order.run?.toString() || '',
      remains: order.remains?.toString() || '',
      accBuff: order.accBuff || '',
      note: order.note || '',
      isReel: order.isReel || false,
      isGroup: order.isGroup || false,
      isCancel: order.isCancel || false,
      isRefill: order.isRefill || false
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (orderId: number) => {
    try {
      const response = await apiRequest(`/api/orders/${orderId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchOrders();
        showNotification('success', 'Xóa đơn hàng thành công!');
      } else {
        showNotification('error', 'Không thể xóa đơn hàng');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    }
  };

  const resetForm = () => {
    setFormData({
      idSmm: '',
      user: '',
      idApi: '',
      provider: '',
      serviceId: '',
      link: '',
      uid: '',
      quantity: '',
      comment: '',
      status: 'pending',
      priceSystem: '',
      priceApi: '',
      // Advanced fields
      start: '0',
      run: '0',
      remains: '0',
      accBuff: '',
      note: '',
      isReel: false,
      isGroup: false,
      isCancel: false,
      isRefill: false
    });
    setEditingOrder(null);
    setShowCreateModal(false);
    setShowAdvancedEdit(false);
  };

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 3000);
  };

  const copyToClipboard = async (text: string, type: 'id_smm' | 'id_api' | 'uid') => {
    try {
      await navigator.clipboard.writeText(text);
      const typeName = type === 'id_smm' ? 'ID SMM' : type === 'id_api' ? 'ID API' : 'UID';
      showNotification('success', `Đã copy ${typeName}: ${truncateText(text, 60)}`);
    } catch (err) {
      showNotification('error', 'Không thể copy vào clipboard');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders(new Set());
    } else {
      const allOrderIds = new Set(orders.map(order => order.idSmm));
      setSelectedOrders(allOrderIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOrder = (orderId: number, event?: React.MouseEvent<HTMLElement>, orderIndex?: number) => {
    const newSelected = new Set(selectedOrders);

    // Check if Shift key is pressed and we have a last selected index
    if (event?.shiftKey && lastSelectedIndex !== null && orderIndex !== undefined) {
      const startIndex = Math.min(lastSelectedIndex, orderIndex);
      const endIndex = Math.max(lastSelectedIndex, orderIndex);

      // Select all orders in the range (including both start and end)
      for (let i = startIndex; i <= endIndex; i++) {
        if (filteredOrders[i]) {
          newSelected.add(filteredOrders[i].idSmm);
        }
      }

      // Update last selected index to the current one
      setLastSelectedIndex(orderIndex);
    } else {
      // Normal single selection - toggle
      if (newSelected.has(orderId)) {
        newSelected.delete(orderId);
      } else {
        newSelected.add(orderId);
      }

      // Update last selected index for future range selections
      if (orderIndex !== undefined) {
        setLastSelectedIndex(orderIndex);
      }
    }

    setSelectedOrders(newSelected);
    setSelectAll(newSelected.size === filteredOrders.length);
  };

  // Reset selection when orders change (pagination, etc.)
  const resetSelection = () => {
    setSelectedOrders(new Set());
    setSelectAll(false);
    setLastSelectedIndex(null);
  };

  // Bulk copy functions
  const bulkCopyIds = async (type: 'id_smm' | 'id_api' | 'uid') => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }

    const selectedOrdersList = orders.filter(order => selectedOrders.has(order.idSmm));
    let ids: string[];

    if (type === 'id_smm') {
      ids = selectedOrdersList.map(order => order.idSmm.toString());
    } else if (type === 'id_api') {
      ids = selectedOrdersList
        .filter(order => order.idApi) // Only orders with idApi
        .map(order => order.idApi);
    } else {
      // type === 'uid'
      ids = selectedOrdersList
        .filter(order => order.uid && order.uid.trim() !== '') // Only orders with uid
        .map(order => order.uid!);
    }

    if (ids.length === 0) {
      const typeName = type === 'id_smm' ? 'ID SMM' : type === 'id_api' ? 'ID API' : 'UID';
      showNotification('error', `Không có ${typeName} để copy`);
      return;
    }

    // Show format selection modal
    setPendingCopyData({ ids, type });
    setShowFormatModal(true);
  };

  // Execute copy with selected format
  const executeCopy = async (format: 'comma' | 'newline') => {
    if (!pendingCopyData) return;

    const { ids, type } = pendingCopyData;
    const idsText = format === 'comma' ? ids.join(', ') : ids.join('\n');

    try {
      await navigator.clipboard.writeText(idsText);
      const typeName = type === 'id_smm' ? 'ID SMM' : type === 'id_api' ? 'ID API' : 'UID';
      const formatName = format === 'comma' ? 'dấu phẩy' : 'xuống dòng';
      showNotification('success', `Đã copy ${ids.length} ${typeName} (${formatName}): ${idsText.length > 50 ? idsText.substring(0, 50) + '...' : idsText}`);
    } catch (err) {
      showNotification('error', 'Không thể copy vào clipboard');
    }

    // Close modal and reset
    setShowFormatModal(false);
    setPendingCopyData(null);
  };

  // Bulk status update function
  const bulkUpdateStatus = async () => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }

    if (selectedOrders.size > 5000) {
      showNotification('error', 'Tối đa 5000 đơn hàng cho mỗi lần cập nhật');
      return;
    }

    try {
      setPageLoading(true);
      const orderIds = Array.from(selectedOrders);

      const response = await apiRequest('/api/orders/bulk/status', {
        method: 'PUT',
        body: JSON.stringify({
          orderIds,
          status: bulkStatus,
          updateWebsite: bulkUpdateWebsite
        })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchOrders(false); // Refresh data
        setShowBulkStatusModal(false);
        setBulkUpdateWebsite(false); // Reset checkbox
        resetSelection();
        showNotification('success', data.message);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setPageLoading(false);
    }
  };

  // Bulk update provider function
  const bulkUpdateProvider = async () => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }

    if (selectedOrders.size > 5000) {
      showNotification('error', 'Tối đa 5000 đơn hàng cho mỗi lần cập nhật provider');
      return;
    }

    if (!bulkProviderName.trim()) {
      showNotification('error', 'Vui lòng nhập tên provider');
      return;
    }

    try {
      setPageLoading(true);
      const orderIds = Array.from(selectedOrders);

      const response = await apiRequest('/api/orders/bulk/update-provider', {
        method: 'PUT',
        body: JSON.stringify({
          orderIds,
          provider: bulkProviderName.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchOrders(false);
        setShowBulkProviderModal(false);
        setBulkProviderName('');
        resetSelection();
        showNotification('success', data.message);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setPageLoading(false);
    }
  };

  // Bulk cancel function
  const handleBulkCancel = async () => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }

    if (selectedOrders.size > BULK_CANCEL_MAX_ORDERS) {
      showNotification('error', `Tối đa ${BULK_CANCEL_MAX_ORDERS} đơn hàng mỗi lần hủy hàng loạt`);
      return;
    }

    // Check for orders that already have cancel request sent
    const selectedOrdersList = orders.filter(order => selectedOrders.has(order.idSmm));
    const alreadyRequestedOrders = selectedOrdersList.filter(order => order.cancelRequested);

    // If there are orders that already have cancel requests, show confirmation
    if (alreadyRequestedOrders.length > 0) {
      const confirmMessage = `Có ${alreadyRequestedOrders.length} đơn hàng đã gửi yêu cầu hủy trước đó. Bạn có muốn gửi yêu cầu hủy lại lần nữa không?`;
      if (!window.confirm(confirmMessage)) {
        return; // User cancelled
      }
    }

    try {
      setBulkCancelLoading(true);
      const orderIds = selectedOrdersList.map(order => order.idSmm);

      const response = await apiRequest('/api/orders/bulk-cancel-provider', {
        method: 'POST',
        body: JSON.stringify({ orderIds })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchOrders(false); // Refresh data
        resetSelection();

        const successCount = data.results.successful.length;
        const failCount = data.results.failed.length;
        showNotification('success', `Hoàn thành: ${successCount} thành công, ${failCount} thất bại`);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setBulkCancelLoading(false);
      setShowBulkCancelModal(false);
      setIsLossCancel(false);
    }
  };

  // Bulk LOSS cancel function (cancel on panel without provider refund)
  const handleBulkLossCancel = async () => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }
    if (selectedOrders.size > 100) {
      showNotification('error', 'Tối đa 100 đơn mỗi lần');
      return;
    }

    try {
      setBulkCancelLoading(true);
      const orderIds = Array.from(selectedOrders);

      const response = await apiRequest('/api/orders/bulk-loss-cancel', {
        method: 'POST',
        body: JSON.stringify({ orderIds })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchOrders(false);
        resetSelection();
        showNotification('success', data.message || 'Hoàn tất hủy lỗ');
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setBulkCancelLoading(false);
      setShowBulkCancelModal(false);
      setIsLossCancel(false);
    }
  };

  // Bulk refill function
  const handleBulkRefill = async () => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }

    if (selectedOrders.size > 100) {
      showNotification('error', 'Tối đa 100 đơn hàng mỗi lần bảo hành hàng loạt');
      return;
    }

    // Check for orders that are not completed
    const selectedOrdersList = orders.filter(order => selectedOrders.has(order.idSmm));
    const notCompletedOrders = selectedOrdersList.filter(order => {
      const status = (order.status || '').toLowerCase();
      return status !== 'completed' && status !== 'hoàn thành';
    });

    if (notCompletedOrders.length > 0) {
      showNotification('error', `Có ${notCompletedOrders.length} đơn hàng chưa hoàn thành. Chỉ có thể bảo hành đơn hàng đã hoàn thành.`);
      return;
    }

    try {
      setBulkRefillLoading(true);
      const orderIds = selectedOrdersList.map(order => order.idSmm);

      const response = await apiRequest('/api/orders/bulk-refill-provider', {
        method: 'POST',
        body: JSON.stringify({ orderIds })
      });

      if (response.ok) {
        const data = await response.json();
        await fetchOrders(false); // Refresh data
        resetSelection();

        const successCount = data.results.successful.length;
        const failCount = data.results.failed.length;
        showNotification('success', `Hoàn thành: ${successCount} thành công, ${failCount} thất bại`);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setBulkRefillLoading(false);
      setShowBulkRefillModal(false);
    }
  };

  // Check provider status function
  const handleCheckProviderStatus = async () => {
    if (selectedOrders.size === 0) {
      showNotification('error', 'Vui lòng chọn ít nhất một đơn hàng');
      return;
    }

    if (selectedOrders.size > 100) {
      showNotification('error', 'Tối đa 100 đơn hàng mỗi lần kiểm tra');
      return;
    }

    try {
      setCheckStatusLoading(true);
      setCheckStatusResult(null);
      const orderIds = Array.from(selectedOrders);

      const response = await apiRequest('/api/orders/check-provider-status', {
        method: 'POST',
        body: JSON.stringify({
          orderIds,
          desiredStatus: checkStatusDesired,
          updateStatusForRefund: checkStatusDesired === 'refunded' && checkStatusUpdateDb
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCheckStatusResult(data);
        if (data.updatedToInprogress > 0) {
          await fetchOrders(false); // Refresh if status was updated
        }
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    } finally {
      setCheckStatusLoading(false);
    }
  };

  // Reset check status modal
  const resetCheckStatusModal = () => {
    setShowCheckStatusModal(false);
    setCheckStatusResult(null);
    setCheckStatusDesired('completed');
    setCheckStatusUpdateDb(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    // Reset singleUpdateWebsite checkbox when status changes away from completed
    if (name === 'status' && value !== 'completed') {
      setSingleUpdateWebsite(false);
    }

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Remove client-side filtering since we're now doing server-side search
  const filteredOrders = orders;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Dùng UTC để tránh lệch múi giờ do chuỗi ISO có 'Z' (server UTC)
    const dt = new Date(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    const d = pad(dt.getUTCDate());
    const m = pad(dt.getUTCMonth() + 1);
    const y = dt.getUTCFullYear();
    const hh = pad(dt.getUTCHours());
    const mm = pad(dt.getUTCMinutes());
    const ss = pad(dt.getUTCSeconds());
    return `${d}/${m}/${y} ${hh}:${mm}:${ss}`;
  };

  const getStatusBadge = (status: string, cancelRequested?: boolean) => {
    // If cancel request has been sent AND status is inprogress, show special status
    if (cancelRequested && status === 'inprogress') {
      return (
        <span style={{
          backgroundColor: '#fef3c7',
          color: '#d97706',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          border: '1px solid #f59e0b'
        }}>
          Đã gửi yêu cầu hủy
        </span>
      );
    }

    const statusStyles = {
      pending: { backgroundColor: '#fef3c7', color: '#d97706', text: 'Chờ xử lý' },
      inprogress: { backgroundColor: '#dbeafe', color: '#2563eb', text: 'Đang xử lý' },
      completed: { backgroundColor: '#dcfce7', color: '#16a34a', text: 'Hoàn thành' },
      pause: { backgroundColor: '#fef3c7', color: '#d97706', text: 'Tạm dừng' },
      refund: { backgroundColor: '#fecaca', color: '#dc2626', text: 'Hoàn tiền' },
      wait_refund: { backgroundColor: '#f3e8ff', color: '#7c3aed', text: 'Chờ hoàn tiền' },
      special: { backgroundColor: '#ecfdf5', color: '#10b981', text: 'Đặc biệt' },
    };

    const style = statusStyles[status as keyof typeof statusStyles] || statusStyles.pending;

    return (
      <span style={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {style.text}
      </span>
    );
  };


  // Check if user has permission for orders module
  if (!hasAnyPermission('orders')) {
    return (
      <Layout currentPage="orders">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px' }}>🔒</div>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#374151',
            textAlign: 'center'
          }}>
            Không có quyền truy cập
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Bạn không có quyền truy cập module này. Vui lòng liên hệ quản trị viên.
          </div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout currentPage="orders">
      <div style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0
      }}>
        {/* Header Actions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: window.innerWidth <= 768 ? '100%' : 'auto'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              minWidth: window.innerWidth <= 768 ? '100%' : '500px',
              flex: window.innerWidth <= 768 ? '1' : 'none'
            }}>
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  minWidth: '140px'
                }}
              >
                <option value="all">Tất cả (tương đối)</option>
                <option value="idSmm">ID SMM (tuyệt đối)</option>
                <option value="idApi">ID API (tuyệt đối)</option>
                <option value="user">User (tuyệt đối)</option>
                <option value="provider">Provider (tuyệt đối)</option>
                <option value="serviceId">Service ID (tuyệt đối)</option>
                <option value="uid">UID (tuyệt đối)</option>
                <option value="link">Link (tương đối)</option>
                <option value="note">Note (tương đối)</option>
              </select>
              <input
                type="text"
                placeholder={
                  searchField === 'all'
                    ? "Tìm kiếm đơn hàng... Nhiều ID: 123,456,789"
                    : searchField === 'idSmm'
                    ? "Nhập ID SMM chính xác"
                    : searchField === 'idApi'
                    ? "Nhập ID API chính xác"
                    : searchField === 'user'
                    ? "Nhập tên user chính xác"
                    : searchField === 'provider'
                    ? "Nhập provider chính xác"
                    : searchField === 'serviceId'
                    ? "Nhập Service ID. Nhiều: 123,456,789"
                    : searchField === 'uid'
                    ? "Nhập UID. Nhiều: uid1,uid2,uid3"
                    : searchField === 'link'
                    ? "Tìm kiếm trong link"
                    : "Tìm kiếm trong note"
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  flex: '1',
                  minWidth: '200px'
                }}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Tất cả trạng thái</option>
              <option value="pending">Chờ xử lý</option>
              <option value="inprogress">Đang xử lý</option>
              <option value="completed">Hoàn thành</option>
              <option value="pause">Tạm dừng</option>
              <option value="refund">Hoàn tiền</option>
              <option value="wait_refund">Chờ hoàn tiền</option>
              <option value="cancel_requested">Đã gửi yêu cầu hủy</option>
              <option value="special">Đặc biệt</option>
            </select>
            <select
              value={stuckOrdersFilter}
              onChange={(e) => setStuckOrdersFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: stuckOrdersFilter ? '#fef3c7' : 'white',
                color: stuckOrdersFilter ? '#d97706' : 'inherit',
                fontWeight: stuckOrdersFilter ? '500' : 'normal'
              }}
            >
              <option value="">🔍 Đơn hàng kẹt</option>
              <option value="15days">⚠️ Kẹt 15 ngày</option>
              <option value="1month">🔶 Kẹt 1 tháng</option>
              <option value="2months">🔸 Kẹt 2 tháng</option>
              <option value="3months">🟠 Kẹt 3 tháng</option>
              <option value="6months">🟡 Kẹt 6 tháng</option>
              <option value="1year">🔴 Kẹt 1 năm</option>
              <option value="2years">⛔ Kẹt 2 năm</option>
            </select>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: zeroRunFilter ? '#fef3c7' : 'white',
                color: zeroRunFilter ? '#d97706' : 'inherit',
                fontWeight: zeroRunFilter ? '500' : 'normal'
              }}
            >
              <input
                type="checkbox"
                checked={zeroRunFilter}
                onChange={(e) => setZeroRunFilter(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              🚫 Run = 0
            </label>
            <select
              aria-label="Số mã ID API"
              value={idApiCountFilter}
              onChange={event => setIdApiCountFilter(event.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: idApiCountFilter ? '#eef2ff' : 'white',
                color: idApiCountFilter ? '#4338ca' : 'inherit',
                fontWeight: idApiCountFilter ? '500' : 'normal'
              }}
            >
              <option value="">ID API: Tất cả</option>
              <option value="none">ID API: Chưa có mã</option>
              <option value="single">ID API: 1 mã</option>
              <option value="multiple">ID API: Nhiều mã (&gt;1)</option>
            </select>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: lossCancelFilter ? '#fef2f2' : 'white',
                color: lossCancelFilter ? '#dc2626' : 'inherit',
                fontWeight: lossCancelFilter ? '500' : 'normal'
              }}
            >
              <input
                type="checkbox"
                checked={lossCancelFilter}
                onChange={(e) => setLossCancelFilter(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              🔥 Đã hủy lỗ
            </label>
            <input
              type="text"
              placeholder="Provider / Service ID"
              value={providerServiceFilter}
              onChange={(e) => setProviderServiceFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: `1px solid ${providerServiceFilter ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '160px',
                maxWidth: '200px',
                backgroundColor: providerServiceFilter ? '#eff6ff' : 'white',
                color: providerServiceFilter ? '#1d4ed8' : 'inherit',
                fontWeight: providerServiceFilter ? '500' : 'normal'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => fetchOrders(false)}
              className="btn-secondary"
              style={{ fontSize: '14px', padding: '8px 12px' }}
            >
              🔄 Làm mới
            </button>
            {hasPermission('orders', 'create') && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                ➕ Tạo đơn hàng
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fecaca',
            border: '1px solid #f87171',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Multi-ID Search Summary */}
        {totalSearchedIds > 1 && (
          <div style={{
            backgroundColor: '#e0f2fe',
            border: '1px solid #0ea5e9',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#0369a1',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>ℹ️</span>
            <span>
              Đang tìm kiếm <strong>{totalSearchedIds}</strong> đơn hàng.
              {' '}Tìm thấy <strong style={{ color: '#059669' }}>{totalFoundIds}</strong> đơn hàng.
              {notFoundOrders.length > 0 && (
                <span style={{ color: '#d97706', marginLeft: '4px' }}>
                  Không tìm thấy <strong>{notFoundOrders.length}</strong> đơn hàng.
                </span>
              )}
            </span>
          </div>
        )}

        {/* Not Found Orders */}
        {notFoundOrders.length > 0 && (
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#d97706'
                }}>
                  Không tìm thấy {notFoundOrders.length} đơn hàng
                </span>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(notFoundOrders.join(','));
                  // Show temporary success message
                  const btn = document.activeElement as HTMLButtonElement;
                  const originalText = btn.textContent;
                  btn.textContent = '✅ Đã copy';
                  btn.style.backgroundColor = '#10b981';
                  btn.style.color = 'white';
                  setTimeout(() => {
                    btn.textContent = originalText;
                    btn.style.backgroundColor = '#f59e0b';
                    btn.style.color = 'white';
                  }, 1500);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.backgroundColor = '#d97706';
                  target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.backgroundColor = '#f59e0b';
                  target.style.transform = 'scale(1)';
                }}
              >
                📋 Copy danh sách
              </button>
            </div>
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fbbf24',
              borderRadius: '6px',
              padding: '12px',
              fontSize: '13px',
              color: '#92400e',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              lineHeight: '1.5'
            }}>
              {notFoundOrders.join(', ')}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#92400e',
              marginTop: '8px',
              fontStyle: 'italic'
            }}>
              💡 Các ID này không tồn tại trong hệ thống hoặc không khớp với bộ lọc hiện tại
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px'
            }}>
              <div className="loading-spinner" style={{ width: '30px', height: '30px' }}></div>
            </div>
          ) : (
            <>
              {/* Bulk actions bar - Fixed position to prevent layout shift */}
              {selectedOrders.size > 0 && (
                <div style={{
                  position: 'fixed',
                  top: '80px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  backgroundColor: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '400px',
                  maxWidth: '90vw',
                  animation: 'slideDown 0.3s ease-out'
                }}>
                  <div>
                    <span style={{
                      fontSize: '14px',
                      color: '#1e40af',
                      fontWeight: '500'
                    }}>
                      Đã chọn {selectedOrders.size} đơn hàng
                    </span>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      💡 Shift+click để chọn khoảng
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => bulkCopyIds('id_smm')}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Copy tất cả ID SMM đã chọn, cách nhau bởi dấu phẩy"
                    >
                      📋 Copy ID SMM
                    </button>

                    {canViewIdApi && (
                      <button
                        onClick={() => bulkCopyIds('id_api')}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#6366f1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Copy tất cả ID API đã chọn, cách nhau bởi dấu phẩy"
                      >
                        📋 Copy ID API
                      </button>
                    )}

                    <button
                      onClick={() => bulkCopyIds('uid')}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Copy tất cả UID đã chọn, cách nhau bởi dấu phẩy"
                    >
                      📋 Copy UID
                    </button>

                    {hasPermission('orders', 'bulk_update') && (
                      <button
                        onClick={() => setShowBulkStatusModal(true)}
                        style={{
                        padding: '6px 12px',
                        backgroundColor: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Cập nhật trạng thái hàng loạt"
                    >
                      🔄 Cập nhật trạng thái
                    </button>
                    )}

                    {hasPermission('orders', 'bulk_cancel') && (
                      <button
                        onClick={() => setShowBulkCancelModal(true)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)'
                      }}
                      title="Hủy đơn hàng hàng loạt"
                      onMouseEnter={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.backgroundColor = '#b91c1c';
                        target.style.transform = 'scale(1.05)';
                        target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.backgroundColor = '#dc2626';
                        target.style.transform = 'scale(1)';
                        target.style.boxShadow = 'none';
                      }}
                    >
                      ❌ Hủy hàng loạt
                    </button>
                    )}

                    {hasPermission('orders', 'bulk_refill') && (
                      <button
                        onClick={() => setShowBulkRefillModal(true)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)'
                      }}
                      title="Bảo hành đơn hàng hàng loạt"
                      onMouseEnter={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.backgroundColor = '#047857';
                        target.style.transform = 'scale(1.05)';
                        target.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.backgroundColor = '#059669';
                        target.style.transform = 'scale(1)';
                        target.style.boxShadow = 'none';
                      }}
                    >
                      ♻️ Bảo hành hàng loạt
                    </button>
                    )}

                    {hasPermission('orders', 'check_status') && (
                    <button
                      onClick={() => setShowCheckStatusModal(true)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#0891b2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      title="Kiểm tra trạng thái đơn hàng từ provider"
                      onMouseEnter={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.backgroundColor = '#0e7490';
                        target.style.transform = 'scale(1.05)';
                        target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.backgroundColor = '#0891b2';
                        target.style.transform = 'scale(1)';
                        target.style.boxShadow = 'none';
                      }}
                    >
                      🔍 Kiểm tra trạng thái
                    </button>
                    )}

                    {hasPermission('orders', 'delete') && (
                      <button
                        onClick={() => {
                          setConfirmModal({
                            open: true,
                            title: 'Xác nhận xóa hàng loạt',
                            message: `Bạn có chắc chắn muốn XÓA VĨNH VIỄN ${selectedOrders.size} đơn hàng đã chọn? Hành động này không thể hoàn tác!`,
                            onConfirm: async () => {
                              try {
                                const response = await apiRequest('/api/orders/bulk/delete', {
                                  method: 'DELETE',
                                  body: JSON.stringify({ orderIds: Array.from(selectedOrders) })
                                });
                                const result = await response.json();
                                if (response.ok) {
                                  showNotification('success', result.message || `Đã xóa ${result.data?.deletedCount} đơn hàng`);
                                  resetSelection();
                                  await fetchOrders();
                                } else {
                                  showNotification('error', result.error || 'Lỗi xóa đơn hàng');
                                }
                              } catch (error) {
                                showNotification('error', 'Lỗi kết nối server');
                              }
                              setConfirmModal(v => ({ ...v, open: false }));
                            }
                          });
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#7f1d1d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease'
                        }}
                        title="Xóa vĩnh viễn các đơn hàng đã chọn"
                        onMouseEnter={(e) => {
                          const target = e.target as HTMLButtonElement;
                          target.style.backgroundColor = '#450a0a';
                          target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.target as HTMLButtonElement;
                          target.style.backgroundColor = '#7f1d1d';
                          target.style.transform = 'scale(1)';
                        }}
                      >
                        🗑️ Xóa hàng loạt
                      </button>
                    )}

                    {hasPermission('orders', 'clear_id_api') && (
                      <button
                        onClick={() => {
                          setConfirmModal({
                            open: true,
                            title: 'Xác nhận xóa ID API',
                            message: `Bạn có chắc chắn muốn XÓA ID API của ${selectedOrders.size} đơn hàng đã chọn? Giá trị ID API sẽ được đặt thành rỗng.`,
                            onConfirm: async () => {
                              try {
                                const response = await apiRequest('/api/orders/bulk/clear-id-api', {
                                  method: 'PUT',
                                  body: JSON.stringify({ orderIds: Array.from(selectedOrders) })
                                });
                                const result = await response.json();
                                if (response.ok) {
                                  showNotification('success', result.message || `Đã xóa ID API của ${result.data?.clearedCount} đơn hàng`);
                                  resetSelection();
                                  await fetchOrders();
                                } else {
                                  showNotification('error', result.error || 'Lỗi xóa ID API');
                                }
                              } catch (error) {
                                showNotification('error', 'Lỗi kết nối server');
                              }
                              setConfirmModal(v => ({ ...v, open: false }));
                            }
                          });
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#9333ea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease'
                        }}
                        title="Xóa ID API của các đơn hàng đã chọn"
                        onMouseEnter={(e) => {
                          const target = e.target as HTMLButtonElement;
                          target.style.backgroundColor = '#7e22ce';
                          target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.target as HTMLButtonElement;
                          target.style.backgroundColor = '#9333ea';
                          target.style.transform = 'scale(1)';
                        }}
                      >
                        🔗 Xóa ID API
                      </button>
                    )}

                    {hasPermission('orders', 'bulk_update') && canViewProvider && (
                      <button
                        onClick={() => setShowBulkProviderModal(true)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#0d9488',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease'
                        }}
                        title="Cập nhật nhà cung cấp hàng loạt"
                        onMouseEnter={(e) => {
                          const target = e.target as HTMLButtonElement;
                          target.style.backgroundColor = '#0f766e';
                          target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.target as HTMLButtonElement;
                          target.style.backgroundColor = '#0d9488';
                          target.style.transform = 'scale(1)';
                        }}
                      >
                        🏢 Đổi Provider
                      </button>
                    )}

                    <button
                      onClick={resetSelection}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                      title="Bỏ chọn tất cả"
                    >
                      Bỏ chọn
                    </button>
                  </div>
                </div>
              )}

              {/* Overall summary of all filtered results */}
              {!loading && totalItems > 0 && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>
                      📦 Tổng số lượng:
                    </span>
                    <span style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>
                      {aggregates.totalQuantity.toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>
                      💰 Tổng tiền:
                    </span>
                    <span style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>
                      {formatCurrency(aggregates.totalPrice)}
                    </span>
                  </div>
                  {incompleteCount > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#d97706', fontWeight: '600' }}>
                        ⏳ Chưa xong:
                      </span>
                      <span style={{ fontSize: '15px', color: '#92400e', fontWeight: '700' }}>
                        {incompleteCount.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {aggregates.totalRemainingPrice > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>
                        💸 Tiền còn lại:
                      </span>
                      <span style={{ fontSize: '15px', color: '#991b1b', fontWeight: '700' }}>
                        {formatCurrency(aggregates.totalRemainingPrice)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Summary of selected orders */}
              {selectedOrders.size > 0 && (() => {
                const selectedOrdersList = orders.filter(order => selectedOrders.has(order.idSmm));
                const totalQuantity = selectedOrdersList.reduce((sum, order) => sum + order.quantity, 0);
                const totalPrice = selectedOrdersList.reduce((sum, order) => sum + (order.priceSystem * order.quantity), 0);
                const incompleteSelected = selectedOrdersList.filter(order => ['pending', 'inprogress'].includes(order.status)).length;

                return (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#0369a1', fontWeight: '600' }}>
                          📊 Tổng số lượng:
                        </span>
                        <span style={{ fontSize: '16px', color: '#0c4a6e', fontWeight: '700' }}>
                          {totalQuantity.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#0369a1', fontWeight: '600' }}>
                          💰 Tổng tiền:
                        </span>
                        <span style={{ fontSize: '16px', color: '#0c4a6e', fontWeight: '700' }}>
                          {formatCurrency(totalPrice)}
                        </span>
                      </div>
                      {incompleteSelected > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '14px', color: '#d97706', fontWeight: '600' }}>
                            ⏳ Chưa xong:
                          </span>
                          <span style={{ fontSize: '16px', color: '#92400e', fontWeight: '700' }}>
                            {incompleteSelected.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#0369a1' }}>
                      ({selectedOrders.size} đơn hàng đã chọn)
                    </div>
                  </div>
                );
              })()}

              <div style={{
                overflowX: 'auto',
                width: '100%',
                maxWidth: '100%',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '0 0 12px 12px',
                position: 'relative'
              }}>
                {/* Loading overlay for pagination */}
                {pageLoading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: '0 0 12px 12px'
                  }}>
                    <div className="loading-spinner" style={{ width: '24px', height: '24px' }}></div>
                  </div>
                )}
                <table style={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                minWidth: '1600px',
                overflowWrap: 'anywhere',
                opacity: pageLoading ? 0.6 : 1,
                transition: 'opacity 0.2s ease-in-out'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#475569', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        style={{
                          cursor: 'pointer',
                          transform: 'scale(1.1)'
                        }}
                        title="Chọn tất cả"
                      />
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>ID SMM</th>
                    {canViewIdApi && <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', width: '160px', boxSizing: 'border-box' }}>ID API</th>}
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Người dùng</th>
                    {canViewProvider && <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Nhà cung cấp</th>}
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Dịch vụ</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', minWidth: '120px' }}>Link</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>UID</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Số lượng</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Trạng thái</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Tiến độ</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Tổng tiền</th>
                    {canViewProfit && <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Lợi nhuận</th>}
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Ngày tạo</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, filteredIndex) => (
                    <tr
                      key={order.idSmm}
                      style={{
                        borderBottom: filteredIndex === filteredOrders.length - 1 ? 'none' : '1px solid #f1f5f9',
                        backgroundColor: selectedOrders.has(order.idSmm) ? '#eff6ff' : 'white'
                      }}
                      onClick={(e) => {
                        // Only trigger if clicking on the row itself, not on buttons
                        if ((e.target as HTMLElement).closest('button')) {
                          return;
                        }
                        e.stopPropagation();
                        handleSelectOrder(order.idSmm, e, filteredIndex);
                      }}
                    >
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedOrders.has(order.idSmm)}
                            readOnly
                            style={{
                              cursor: 'pointer',
                              transform: 'scale(1.1)',
                              pointerEvents: 'none' // Disable direct clicking on checkbox
                            }}
                            title="Shift+click để chọn khoảng"
                          />
                        </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer'
                        }}
                        onClick={() => copyToClipboard(order.idSmm.toString(), 'id_smm')}
                        title="Click để copy ID SMM"
                      >
                        #{order.idSmm}
                      </td>
                      {canViewIdApi && (
                        <OrderApiIdsCell orderId={order.idSmm} value={order.idApi} onCopy={value => copyToClipboard(value, 'id_api')} />
                      )}
                      <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                        <div title={order.user} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.user}</div>
                      </td>
                      {canViewProvider && (
                        <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                          <div title={order.provider} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.provider}</div>
                        </td>
                      )}
                      <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                        {order.serviceId}
                      </td>
                      <td style={{
                        padding: '12px',
                        fontSize: '13px',
                        color: '#475569',
                        maxWidth: expandedLinks.has(order.idSmm) ? 'none' : '150px',
                        wordBreak: expandedLinks.has(order.idSmm) ? 'break-all' : 'normal',
                        whiteSpace: expandedLinks.has(order.idSmm) ? 'normal' : 'nowrap'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                          <span
                            onClick={() => toggleExpandLink(order.idSmm)}
                            style={{
                              cursor: 'pointer',
                              color: '#2563eb',
                              textDecoration: 'none',
                              flex: 1,
                              minWidth: 0,
                              overflow: expandedLinks.has(order.idSmm) ? 'visible' : 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={expandedLinks.has(order.idSmm) ? 'Click để thu gọn' : 'Click để xem đầy đủ'}
                          >
                            {expandedLinks.has(order.idSmm) ? order.link : truncateText(order.link, 20)}
                          </span>
                          <a
                            href={order.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              flexShrink: 0
                            }}
                            title="Mở link trong tab mới"
                          >
                            🔗
                          </a>
                        </div>
                      </td>
                      <td style={{
                        padding: '12px',
                        fontSize: '13px',
                        color: order.uid ? '#8b5cf6' : '#475569',
                        maxWidth: expandedUids.has(order.idSmm) ? 'none' : '150px',
                        wordBreak: expandedUids.has(order.idSmm) ? 'break-all' : 'normal',
                        whiteSpace: expandedUids.has(order.idSmm) ? 'normal' : 'nowrap',
                        fontWeight: order.uid ? '500' : 'normal'
                      }}>
                        {order.uid ? (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                            <span
                              onClick={() => toggleExpandUid(order.idSmm)}
                              style={{ cursor: 'pointer', flex: 1, minWidth: 0, overflow: expandedUids.has(order.idSmm) ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}
                              title={expandedUids.has(order.idSmm) ? 'Click để thu gọn' : 'Click để xem đầy đủ'}
                            >
                              {expandedUids.has(order.idSmm) ? order.uid : truncateText(order.uid, 20)}
                            </span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(order.uid!, 'uid');
                              }}
                              style={{
                                cursor: 'pointer',
                                fontSize: '12px',
                                flexShrink: 0
                              }}
                              title="Copy UID"
                            >
                              📋
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                        {order.quantity.toLocaleString()}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {getStatusBadge(order.status, order.cancelRequested)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px', color: '#475569' }}>
                        <div>Bắt đầu: {order.start?.toLocaleString() || 0}</div>
                        <div>Đã chạy: {order.run.toLocaleString()}</div>
                        <div>Còn lại: {order.remains.toLocaleString()}</div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                        {formatCurrency(order.totalPriceSystem)}
                      </td>
                      {canViewProfit && (
                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                          {formatCurrency(order.profitOrder)}
                        </td>
                      )}
                      <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>
                        {formatDate(order.created)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ position: 'relative' }}>
                          <button
                            id={`action-btn-${order.idSmm}`}
                            onClick={() => setOpenActionMenu(openActionMenu === order.idSmm ? null : order.idSmm)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#f1f5f9',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: '#475569',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            ⚙️ <span style={{ fontSize: '10px' }}>▼</span>
                          </button>

                          {openActionMenu === order.idSmm && (() => {
                            // Calculate position based on button
                            const btn = document.getElementById(`action-btn-${order.idSmm}`);
                            const rect = btn?.getBoundingClientRect();
                            const dropdownTop = (rect?.bottom || 0) + 4;
                            const dropdownRight = window.innerWidth - (rect?.right || 0);

                            return (
                            <>
                              {/* Overlay to close dropdown when clicking outside */}
                              <div
                                onClick={() => setOpenActionMenu(null)}
                                style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 999
                                }}
                              />
                              <div style={{
                                position: 'fixed',
                                top: dropdownTop,
                                right: dropdownRight,
                                backgroundColor: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 1000,
                                minWidth: '160px',
                                overflow: 'hidden'
                              }}>
                                {hasPermission('orders', 'update') && (
                                  <button
                                    onClick={() => {
                                      handleEdit(order);
                                      setOpenActionMenu(null);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 14px',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      fontSize: '13px',
                                      color: '#1d4ed8',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      textAlign: 'left'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    ✏️ Chỉnh sửa
                                  </button>
                                )}
                                {progressServiceIds.includes(order.serviceId) && (
                                  <button
                                    onClick={() => {
                                      setOpenActionMenu(null);
                                      setProgressModal({ open: true, idSmm: order.idSmm.toString(), data: [], loading: true, page: 1, limit: 20, total: 0, totalPages: 0 });
                                      fetchProgressOrders(order.idSmm.toString(), 1);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 14px',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      fontSize: '13px',
                                      color: '#059669',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      textAlign: 'left'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    👁️ Xem Progress
                                  </button>
                                )}
                                {hasPermission('orders', 'cancel') && (
                                  <button
                                    onClick={() => {
                                      setOpenActionMenu(null);

                                      // Check if this is progress-cancel service (from config)
                                      const isProgressCancelService = progressServiceIds.includes(order.serviceId);

                                      // Check if order is older than 24 hours
                                      // Handle timezone: order.created may be stored as Vietnam time (UTC+7)
                                      // Parse the date and compare properly
                                      let orderCreated = new Date(order.created);
                                      const now = new Date();

                                      // If the created date string doesn't have timezone info, treat it as Vietnam time
                                      // and convert to UTC for proper comparison
                                      const createdStr = order.created;
                                      if (createdStr && !createdStr.includes('Z') && !createdStr.includes('+')) {
                                        // No timezone info - assume it's Vietnam time (UTC+7)
                                        // Subtract 7 hours to convert to UTC
                                        orderCreated = new Date(orderCreated.getTime() - (7 * 60 * 60 * 1000));
                                      }

                                      const diffHours = (now.getTime() - orderCreated.getTime()) / (1000 * 60 * 60);
                                      const isOlderThan24Hours = diffHours >= 24;

                                      // Build confirmation message
                                      let confirmTitle = 'Xác nhận hủy đơn';
                                      let confirmMessage = `Gửi yêu cầu hủy đơn #${order.idSmm} tới nhà cung cấp?`;

                                      if (isProgressCancelService && !isOlderThan24Hours) {
                                        confirmTitle = '⚠️ Cảnh báo: Đơn hàng chưa đủ 24 giờ';
                                        confirmMessage = `Đơn hàng #${order.idSmm} mới được tạo ${Math.max(0, diffHours).toFixed(1)} giờ trước (chưa đủ 24 giờ).\n\nBạn có chắc chắn muốn hủy đơn này không?`;
                                      }

                                      // Determine if we need to force cancel (for progress-cancel services < 24h)
                                      const needForce = isProgressCancelService && !isOlderThan24Hours;

                                      setConfirmModal({
                                        open: true,
                                        title: confirmTitle,
                                        message: confirmMessage,
                                        onConfirm: async () => {
                                          try {
                                            const resp = await apiRequest(`/api/orders/${order.idSmm}/cancel-provider`, {
                                              method: 'POST',
                                              body: JSON.stringify({ force: needForce })
                                            });
                                            if (resp.ok) {
                                              showNotification('success', 'Hủy bỏ đơn hàng thành công');
                                              fetchOrders(false); // Refresh data
                                            } else if (resp.status === 403) {
                                              showNotification('error', 'Bạn không có quyền hủy đơn hàng');
                                            } else {
                                              const er = await resp.json();
                                              showNotification('error', er.error || 'Hủy đơn thất bại');
                                            }
                                          } catch {
                                            showNotification('error', 'Lỗi kết nối server');
                                          } finally {
                                            setConfirmModal(v => ({ ...v, open: false }));
                                          }
                                        }
                                      });
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 14px',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      fontSize: '13px',
                                      color: '#92400e',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      textAlign: 'left'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    ❌ Hủy đơn
                                  </button>
                                )}
                                {hasPermission('orders', 'refill') &&
                                  order.provider?.toLowerCase() === 'sabo' &&
                                  order.idApi &&
                                  (order.status?.toLowerCase() === 'completed' || order.status?.toLowerCase() === 'hoàn thành') && (
                                  <button
                                    onClick={() => {
                                      setOpenActionMenu(null);
                                      setConfirmModal({
                                        open: true,
                                        title: 'Xác nhận bảo hành',
                                        message: `Gửi yêu cầu bảo hành (refill) cho đơn #${order.idSmm}?`,
                                        onConfirm: async () => {
                                          try {
                                            const resp = await apiRequest(`/api/orders/${order.idSmm}/refill-provider`, { method: 'POST' });
                                            if (resp.ok) {
                                              showNotification('success', 'Gửi yêu cầu bảo hành thành công');
                                            } else if (resp.status === 403) {
                                              showNotification('error', 'Bạn không có quyền bảo hành đơn hàng');
                                            } else {
                                              const er = await resp.json();
                                              showNotification('error', er.error || 'Gửi yêu cầu bảo hành thất bại');
                                            }
                                          } catch {
                                            showNotification('error', 'Lỗi kết nối server');
                                          } finally {
                                            setConfirmModal(v => ({ ...v, open: false }));
                                          }
                                        }
                                      });
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 14px',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      fontSize: '13px',
                                      color: '#166534',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      textAlign: 'left'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dcfce7'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    ♻️ Bảo hành
                                  </button>
                                )}
                                {hasPermission('orders', 'refill') &&
                                  REFILL_CHECK_SERVICE_IDS.includes(order.serviceId) &&
                                  order.link &&
                                  (order.status?.toLowerCase() === 'completed' || order.status?.toLowerCase() === 'hoàn thành') && (
                                  <button
                                    onClick={async () => {
                                      setOpenActionMenu(null);
                                      try {
                                        const resp = await apiRequest(`/api/orders/${order.idSmm}/refill-playcount-check`, {
                                          method: 'POST'
                                        });
                                        if (resp.ok) {
                                          const data = await resp.json();
                                          const details = data.details;
                                          if (data.success && data.code === 'DROP_DETECTED') {
                                            setViewCheckModal({
                                              open: true,
                                              type: 'success',
                                              title: '⚠️ Phát hiện tụt view',
                                              message: data.message || 'Drop detected, order is eligible for refill.',
                                              details: details ? {
                                                currentViews: details.playCount,
                                                initialViews: details.start,
                                                purchasedQuantity: details.quantity,
                                                minimumExpected: details.expectedMin,
                                                orderId: order.idSmm
                                              } : { orderId: order.idSmm }
                                            });
                                          } else {
                                            setViewCheckModal({
                                              open: true,
                                              type: 'info',
                                              title: '✅ Kết quả kiểm tra view',
                                              message: data.message || 'No drop detected or order is not eligible for refill.',
                                              details: details ? {
                                                currentViews: details.playCount,
                                                initialViews: details.start,
                                                purchasedQuantity: details.quantity,
                                                minimumExpected: details.expectedMin,
                                                orderId: order.idSmm
                                              } : { orderId: order.idSmm }
                                            });
                                          }
                                        } else if (resp.status === 403) {
                                          setViewCheckModal({
                                            open: true,
                                            type: 'error',
                                            title: '❌ Lỗi quyền truy cập',
                                            message: 'Bạn không có quyền kiểm tra refill'
                                          });
                                        } else {
                                          const er = await resp.json();
                                          setViewCheckModal({
                                            open: true,
                                            type: 'error',
                                            title: '❌ Kiểm tra thất bại',
                                            message: er.error || 'Kiểm tra refill thất bại'
                                          });
                                        }
                                      } catch {
                                        setViewCheckModal({
                                          open: true,
                                          type: 'error',
                                          title: '❌ Lỗi kết nối',
                                          message: 'Lỗi kết nối server'
                                        });
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 14px',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      fontSize: '13px',
                                      color: '#0c4a6e',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      textAlign: 'left'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    🔍 Kiểm tra view
                                  </button>
                                )}
                                {hasPermission('orders', 'delete') && (
                                  <>
                                    <div style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '4px 0' }} />
                                    <button
                                      onClick={() => {
                                        setOpenActionMenu(null);
                                        setConfirmModal({
                                          open: true,
                                          title: 'Xác nhận xóa đơn',
                                          message: `Bạn có chắc chắn muốn xóa đơn #${order.idSmm}?`,
                                          onConfirm: async () => {
                                            await handleDelete(order.idSmm);
                                            setConfirmModal(v => ({ ...v, open: false }));
                                          }
                                        });
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: '13px',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        textAlign: 'left'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      🗑️ Xóa đơn
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          );
                          })()}
                        </div>
                        </td>
                      </tr>
                    ))}
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={12 + (canViewIdApi ? 1 : 0) + (canViewProvider ? 1 : 0) + (canViewProfit ? 1 : 0)} style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#94a3b8',
                        fontSize: '14px'
                      }}>
                        {searchTerm || statusFilter || stuckOrdersFilter || idApiCountFilter ? 'Không tìm thấy đơn hàng phù hợp' : 'Chưa có đơn hàng nào'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
          )}

          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '20px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              {/* Items per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Hiển thị:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                  <option value={2000}>2000</option>
                  <option value={5000}>5000</option>
                </select>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>dòng</span>
              </div>

              {/* Pagination info */}
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Hiển thị {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng {totalItems} đơn hàng
                {incompleteCount > 0 && (
                  <span style={{ marginLeft: '12px', color: '#d97706', fontWeight: '600' }}>
                    | ⏳ Chưa xong: <span style={{ color: '#92400e', fontWeight: '700' }}>{incompleteCount.toLocaleString()}</span>
                  </span>
                )}
              </div>

              {/* Pagination buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || pageLoading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: (currentPage === 1 || pageLoading) ? '#f9fafb' : 'white',
                    color: (currentPage === 1 || pageLoading) ? '#9ca3af' : '#374151',
                    cursor: (currentPage === 1 || pageLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Đầu
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || pageLoading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: (currentPage === 1 || pageLoading) ? '#f9fafb' : 'white',
                    color: (currentPage === 1 || pageLoading) ? '#9ca3af' : '#374151',
                    cursor: (currentPage === 1 || pageLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Trước
                </button>

                {/* Page numbers */}
                {(() => {
                  const totalPages = Math.ceil(totalItems / itemsPerPage);
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  // Adjust startPage if we're near the end
                  if (endPage - startPage < maxVisiblePages - 1) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        disabled={pageLoading}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: currentPage === i ? '#3b82f6' : 'white',
                          color: currentPage === i ? 'white' : '#374151',
                          cursor: pageLoading ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: currentPage === i ? '600' : '400',
                          opacity: pageLoading ? 0.6 : 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalItems / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: (currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading) ? '#f9fafb' : 'white',
                    color: (currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading) ? '#9ca3af' : '#374151',
                    cursor: (currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Sau
                </button>
                <button
                  onClick={() => setCurrentPage(Math.ceil(totalItems / itemsPerPage))}
                  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: (currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading) ? '#f9fafb' : 'white',
                    color: (currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading) ? '#9ca3af' : '#374151',
                    cursor: (currentPage >= Math.ceil(totalItems / itemsPerPage) || pageLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cuối
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Status Update Modal */}
        {showBulkStatusModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 16px 0',
                textAlign: 'center'
              }}>
                Cập nhật trạng thái hàng loạt
              </h3>

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 20px 0',
                textAlign: 'center'
              }}>
                Cập nhật trạng thái cho {selectedOrders.size} đơn hàng đã chọn
              </p>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Chọn trạng thái mới:
                </label>
                <select
                  value={bulkStatus}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setBulkStatus(newStatus);
                    // Reset checkbox when status changes away from completed
                    if (newStatus !== 'completed') {
                      setBulkUpdateWebsite(false);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="pending">Chờ xử lý</option>
                  <option value="inprogress">Đang xử lý</option>
                  <option value="completed">Hoàn thành</option>
                  <option value="pause">Tạm dừng</option>
                  <option value="refund">Hoàn tiền</option>
                  <option value="wait_refund">Chờ hoàn tiền</option>
                  <option value="special">Đặc biệt</option>
                </select>
              </div>

              {/* Update website checkbox - only show when status is completed */}
              {bulkStatus === 'completed' && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <input
                    type="checkbox"
                    id="bulkUpdateWebsite"
                    checked={bulkUpdateWebsite}
                    onChange={(e) => setBulkUpdateWebsite(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <label
                    htmlFor="bulkUpdateWebsite"
                    style={{
                      fontSize: '14px',
                      color: '#0369a1',
                      fontWeight: '500',
                      cursor: 'pointer',
                      userSelect: 'none',
                      flex: 1
                    }}
                  >
                    🌐 Cập nhật hoàn thành lên website kinh doanh (qua API v2)
                  </label>
                </div>
              )}

              {bulkStatus === 'completed' && (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '6px',
                  marginBottom: '24px'
                }}>
                  <p style={{
                    fontSize: '13px',
                    color: '#92400e',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    ⚠️ <strong>Lưu ý:</strong> Tối đa 5000 đơn hàng cho mỗi lần cập nhật. Nếu chọn cập nhật lên website, hệ thống sẽ cập nhật cả database và website kinh doanh.
                  </p>
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowBulkStatusModal(false);
                    setBulkUpdateWebsite(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Hủy
                </button>
                <button
                  onClick={bulkUpdateStatus}
                  disabled={pageLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: pageLoading ? '#9ca3af' : '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: pageLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {pageLoading && <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>}
                  Cập nhật {selectedOrders.size} đơn hàng
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Update Provider Modal */}
        {showBulkProviderModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '420px',
              maxWidth: '90%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 16px 0',
                textAlign: 'center'
              }}>
                🏢 Cập nhật nhà cung cấp hàng loạt
              </h3>

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 20px 0',
                textAlign: 'center'
              }}>
                Đổi provider cho <strong>{selectedOrders.size}</strong> đơn hàng đã chọn
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Nhập tên provider mới:
                </label>
                <input
                  type="text"
                  value={bulkProviderName}
                  onChange={(e) => setBulkProviderName(e.target.value)}
                  placeholder="Ví dụ: sabo, mfb, buffviewer_v2..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0d9488';
                    e.target.style.boxShadow = '0 0 0 2px rgba(13, 148, 136, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && bulkProviderName.trim()) {
                      bulkUpdateProvider();
                    }
                  }}
                  autoFocus
                />
              </div>

              <div style={{
                padding: '10px',
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                marginBottom: '24px'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#92400e',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  ⚠️ <strong>Lưu ý:</strong> Tối đa 5000 đơn hàng cho mỗi lần cập nhật. Dữ liệu cột provider sẽ được thay đổi cho tất cả đơn hàng đã chọn.
                </p>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowBulkProviderModal(false);
                    setBulkProviderName('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Hủy
                </button>
                <button
                  onClick={bulkUpdateProvider}
                  disabled={pageLoading || !bulkProviderName.trim()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: (pageLoading || !bulkProviderName.trim()) ? '#9ca3af' : '#0d9488',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: (pageLoading || !bulkProviderName.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {pageLoading && <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>}
                  Cập nhật {selectedOrders.size} đơn hàng
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Cancel Modal */}
        {showBulkCancelModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '500px',
              maxWidth: '90%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 16px 0',
                textAlign: 'center'
              }}>
                Hủy đơn hàng hàng loạt
              </h3>

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 20px 0',
                textAlign: 'center'
              }}>
                Bạn có chắc chắn muốn hủy {selectedOrders.size} đơn hàng đã chọn?
              </p>

              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                    Lưu ý quan trọng:
                  </span>
                </div>
                <ul style={{
                  margin: '0',
                  paddingLeft: '20px',
                  fontSize: '13px',
                  color: '#92400e',
                  lineHeight: '1.4'
                }}>
                  <li>Chỉ các đơn hàng hỗ trợ hủy mới được xử lý</li>
                  <li>Tối đa {BULK_CANCEL_MAX_ORDERS} đơn hàng mỗi lần</li>
                </ul>
              </div>

              {/* Loss cancel checkbox */}
              <div style={{
                padding: '12px',
                backgroundColor: isLossCancel ? '#fef2f2' : '#f8fafc',
                border: `1px solid ${isLossCancel ? '#fecaca' : '#e2e8f0'}`,
                borderRadius: '8px',
                marginBottom: '20px',
                transition: 'all 0.2s'
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isLossCancel}
                    onChange={(e) => setIsLossCancel(e.target.checked)}
                    style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: '#dc2626' }}
                  />
                  <div>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: isLossCancel ? '#dc2626' : '#374151' }}>
                      ⚠️ Hủy lỗ (không hoàn tiền từ provider)
                    </span>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Hủy trên Panel và hoàn tiền cho khách, nhưng KHONG gửi yêu cầu hủy đến provider. Tiền sẽ bị lỗ.
                    </p>
                    {isLossCancel && (
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>
                        ⚠️ Lưu ý: Thao tác này sẽ BỊ LỖ vì không được hoàn tiền từ provider!
                      </p>
                    )}
                  </div>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowBulkCancelModal(false)}
                  disabled={bulkCancelLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: bulkCancelLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!bulkCancelLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#f9fafb';
                      target.style.borderColor = '#9ca3af';
                      target.style.color = '#374151';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!bulkCancelLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = 'white';
                      target.style.borderColor = '#d1d5db';
                      target.style.color = '#6b7280';
                    }
                  }}
                >
                  Hủy bỏ
                </button>
                <button
                  onClick={isLossCancel ? handleBulkLossCancel : handleBulkCancel}
                  disabled={bulkCancelLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: bulkCancelLoading ? '#9ca3af' : '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: bulkCancelLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!bulkCancelLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#b91c1c';
                      target.style.transform = 'scale(1.02)';
                      target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!bulkCancelLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#dc2626';
                      target.style.transform = 'scale(1)';
                      target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {bulkCancelLoading && <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>}
                  {bulkCancelLoading ? 'Đang hủy...' : isLossCancel ? `⚠️ Hủy lỗ ${selectedOrders.size} đơn` : `Hủy ${selectedOrders.size} đơn hàng`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Refill Modal */}
        {showBulkRefillModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '28px',
              width: '450px',
              maxWidth: '90%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              transform: 'scale(1)',
              animation: 'modalFadeIn 0.2s ease-out'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  ♻️
                </div>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    Xác nhận bảo hành hàng loạt
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '4px 0 0 0'
                  }}>
                    {selectedOrders.size} đơn hàng đã chọn
                  </p>
                </div>
              </div>

              <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
                  ⚠️ <strong>Lưu ý:</strong> Chỉ có thể bảo hành đơn hàng đã hoàn thành và provider hỗ trợ bảo hành tự động (hiện tại: sabo).
                </p>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowBulkRefillModal(false)}
                  disabled={bulkRefillLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: bulkRefillLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!bulkRefillLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#f9fafb';
                      target.style.borderColor = '#9ca3af';
                      target.style.color = '#374151';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!bulkRefillLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = 'white';
                      target.style.borderColor = '#d1d5db';
                      target.style.color = '#6b7280';
                    }
                  }}
                >
                  Hủy bỏ
                </button>
                <button
                  onClick={handleBulkRefill}
                  disabled={bulkRefillLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: bulkRefillLoading ? '#9ca3af' : '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: bulkRefillLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!bulkRefillLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#047857';
                      target.style.transform = 'scale(1.02)';
                      target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!bulkRefillLoading) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#059669';
                      target.style.transform = 'scale(1)';
                      target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {bulkRefillLoading && <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>}
                  {bulkRefillLoading ? 'Đang xử lý...' : `Bảo hành ${selectedOrders.size} đơn hàng`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Format Selection Modal */}
        {showFormatModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 16px 0',
                textAlign: 'center'
              }}>
                Chọn định dạng copy
              </h3>

              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 20px 0',
                textAlign: 'center'
              }}>
                Bạn muốn copy {pendingCopyData?.ids.length} ID theo định dạng nào?
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <button
                  onClick={() => executeCopy('comma')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>📋</span>
                  <div>
                    <div>Ngăn cách dấu phẩy</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      Ví dụ: 123, 124, 125
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => executeCopy('newline')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>📝</span>
                  <div>
                    <div>Mỗi ID một dòng</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      Ví dụ: 123<br />124<br />125
                    </div>
                  </div>
                </button>
              </div>

              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => {
                    setShowFormatModal(false);
                    setPendingCopyData(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: window.innerWidth <= 768 ? '16px' : '24px',
              width: '95%',
              maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              margin: window.innerWidth <= 768 ? '16px' : '0'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1e293b',
                margin: '0 0 20px 0'
              }}>
                {editingOrder ? 'Chỉnh sửa đơn hàng' : 'Tạo đơn hàng mới'}
              </h3>


              <form onSubmit={handleSubmit}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      ID Đơn hàng *
                    </label>
                    <input
                      type="number"
                      name="idSmm"
                      required
                      min="1"
                      value={formData.idSmm}
                      onChange={handleChange}
                      disabled={editingOrder ? true : false}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: editingOrder ? '#f9fafb' : 'white'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Người dùng *
                    </label>
                    <input
                      type="text"
                      name="user"
                      required
                      value={formData.user}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {canViewIdApi && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                        API ID (optional)
                      </label>
                      <input
                        type="text"
                        name="idApi"
                        value={formData.idApi}
                        onChange={handleChange}
                        placeholder="Để trống nếu chưa có"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}

                  {canViewProvider && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                        Nhà cung cấp *
                      </label>
                      <input
                        type="text"
                        name="provider"
                        required
                        value={formData.provider}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      ID Dịch vụ *
                    </label>
                    <input
                      type="number"
                      name="serviceId"
                      required
                      value={formData.serviceId}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Link *
                    </label>
                    <input
                      type="url"
                      name="link"
                      required
                      value={formData.link}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      UID *
                    </label>
                    <input
                      type="text"
                      name="uid"
                      required
                      value={formData.uid}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Số lượng *
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      required
                      min="1"
                      value={formData.quantity}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Trạng thái *
                    </label>
                    <select
                      name="status"
                      required
                      value={formData.status}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="pending">Chờ xử lý</option>
                      <option value="inprogress">Đang xử lý</option>
                      <option value="completed">Hoàn thành</option>
                      <option value="pause">Tạm dừng</option>
                      <option value="refund">Hoàn tiền</option>
                      <option value="wait_refund">Chờ hoàn tiền</option>
                      <option value="special">Đặc biệt</option>
                    </select>
                  </div>

                  {/* Update website checkbox - only show when editing and status is completed */}
                  {editingOrder && formData.status === 'completed' && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <input
                        type="checkbox"
                        id="singleUpdateWebsite"
                        checked={singleUpdateWebsite}
                        onChange={(e) => setSingleUpdateWebsite(e.target.checked)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <label
                        htmlFor="singleUpdateWebsite"
                        style={{
                          fontSize: '14px',
                          color: '#0369a1',
                          fontWeight: '500',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        🌐 Cập nhật hoàn thành lên website kinh doanh (qua API v2)
                      </label>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Giá hệ thống *
                    </label>
                    <input
                      type="number"
                      name="priceSystem"
                      required
                      step="0.0001"
                      min="0"
                      value={formData.priceSystem}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {canViewPriceApi && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                        Giá API *
                      </label>
                      <input
                        type="number"
                        name="priceApi"
                        required
                        step="0.0001"
                        min="0"
                        value={formData.priceApi}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      💬 Comment (dịch vụ comment)
                    </label>
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      placeholder="Nội dung comment cho dịch vụ..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Advanced Edit Toggle */}
                  {editingOrder && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                        <input
                          type="checkbox"
                          checked={showAdvancedEdit}
                          onChange={(e) => setShowAdvancedEdit(e.target.checked)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontWeight: '500' }}>✏️ Edit chi tiết (hiển thị tất cả trường dữ liệu)</span>
                      </label>
                    </div>
                  )}

                  {/* Advanced Fields */}
                  {showAdvancedEdit && (
                    <>
                      <div style={{ gridColumn: '1 / -1', padding: '12px 0', borderTop: '1px solid #e5e7eb' }}>
                        <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                          🔧 Trường chi tiết
                        </h4>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Bắt đầu (Start)
                        </label>
                        <input
                          type="number"
                          name="start"
                          min="0"
                          value={formData.start}
                          onChange={handleChange}
                          placeholder="Để trống hoặc 0"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Đã chạy
                        </label>
                        <input
                          type="number"
                          name="run"
                          min="0"
                          value={formData.run}
                          onChange={handleChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Còn lại
                        </label>
                        <input
                          type="number"
                          name="remains"
                          min="0"
                          value={formData.remains}
                          onChange={handleChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          📋 Account Buffer (chỉ đọc)
                        </label>
                        <textarea
                          name="accBuff"
                          value={formData.accBuff}
                          disabled
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                            backgroundColor: '#f9fafb',
                            color: '#6b7280',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          📝 Ghi chú (nội bộ)
                        </label>
                        <input
                          type="text"
                          name="note"
                          value={formData.note}
                          onChange={handleChange}
                          placeholder="Ghi chú nội bộ..."
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                          Tùy chọn đặc biệt
                        </label>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                            <input
                              type="checkbox"
                              name="isReel"
                              checked={formData.isReel}
                              onChange={handleChange}
                              style={{ marginRight: '6px' }}
                            />
                            📹 Is Reel
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                            <input
                              type="checkbox"
                              name="isGroup"
                              checked={formData.isGroup}
                              onChange={handleChange}
                              style={{ marginRight: '6px' }}
                            />
                            👥 Is Group
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                            <input
                              type="checkbox"
                              name="isCancel"
                              checked={formData.isCancel}
                              onChange={handleChange}
                              style={{ marginRight: '6px' }}
                            />
                            ❌ Is Cancel
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                            <input
                              type="checkbox"
                              name="isRefill"
                              checked={formData.isRefill}
                              onChange={handleChange}
                              style={{ marginRight: '6px' }}
                            />
                            🔄 Is Refill
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={resetForm}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ padding: '10px 20px', fontSize: '14px' }}
                  >
                    {editingOrder ? 'Cập nhật' : 'Tạo đơn hàng'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Notification Popup */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          backgroundColor: notification.type === 'success' ? '#10b981' : notification.type === 'error' ? '#ef4444' : '#3b82f6',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px',
          wordWrap: 'break-word',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : 'ℹ️'}</span>
            {notification.message}
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <Modal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal(v => ({ ...v, open: false }))}
        title={confirmModal.title || 'Xác nhận'}
        size="sm"
      >
        <div style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
          {confirmModal.message}
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setConfirmModal(v => ({ ...v, open: false }))}
            style={{
              padding: '8px 12px',
              backgroundColor: 'white',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#f9fafb';
              target.style.borderColor = '#9ca3af';
              target.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = 'white';
              target.style.borderColor = '#d1d5db';
              target.style.color = '#6b7280';
            }}
          >
            Hủy
          </button>
          <button
            onClick={() => confirmModal.onConfirm?.()}
            style={{
              padding: '8px 12px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#1d4ed8';
              target.style.transform = 'scale(1.02)';
              target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#2563eb';
              target.style.transform = 'scale(1)';
              target.style.boxShadow = 'none';
            }}
          >
            Xác nhận
          </button>
        </div>
      </Modal>

      {/* View Check Result Modal */}
      <Modal
        isOpen={viewCheckModal.open}
        onClose={() => setViewCheckModal(v => ({ ...v, open: false }))}
        title={viewCheckModal.title || 'Kết quả kiểm tra'}
        size="md"
      >
        <div style={{
          padding: '16px',
          backgroundColor: viewCheckModal.type === 'success' ? '#f0fdf4' :
                          viewCheckModal.type === 'error' ? '#fef2f2' : '#eff6ff',
          borderRadius: '8px',
          border: `1px solid ${viewCheckModal.type === 'success' ? '#bbf7d0' :
                               viewCheckModal.type === 'error' ? '#fecaca' : '#bfdbfe'}`,
          marginBottom: '16px'
        }}>
          <p style={{
            fontSize: '14px',
            color: viewCheckModal.type === 'success' ? '#166534' :
                   viewCheckModal.type === 'error' ? '#dc2626' : '#1e40af',
            margin: 0,
            lineHeight: '1.6'
          }}>
            {viewCheckModal.message}
          </p>
        </div>

        {viewCheckModal.details && (
          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
              📊 Chi tiết:
            </h4>
            <div style={{ display: 'grid', gap: '8px' }}>
              {viewCheckModal.details.orderId && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#64748b' }}>ID Đơn hàng:</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>#{viewCheckModal.details.orderId}</span>
                </div>
              )}
              {viewCheckModal.details.currentViews !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#64748b' }}>View hiện tại:</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>{viewCheckModal.details.currentViews.toLocaleString()}</span>
                </div>
              )}
              {viewCheckModal.details.initialViews !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#64748b' }}>View ban đầu (start):</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>{viewCheckModal.details.initialViews.toLocaleString()}</span>
                </div>
              )}
              {viewCheckModal.details.purchasedQuantity !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#64748b' }}>Số lượng mua:</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>{viewCheckModal.details.purchasedQuantity.toLocaleString()}</span>
                </div>
              )}
              {viewCheckModal.details.minimumExpected !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#64748b' }}>View tối thiểu cần có:</span>
                  <span style={{ fontWeight: '600', color: viewCheckModal.type === 'success' ? '#dc2626' : '#16a34a' }}>
                    {viewCheckModal.details.minimumExpected.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setViewCheckModal(v => ({ ...v, open: false }))}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#1d4ed8';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#2563eb';
            }}
          >
            Đóng
          </button>
        </div>
      </Modal>

      {/* Check Provider Status Modal */}
      <Modal isOpen={showCheckStatusModal} onClose={resetCheckStatusModal} title="🔍 Kiểm tra trạng thái đơn hàng">
        <div style={{ padding: '16px' }}>
          {checkStatusResult ? (
            // Show results
            <div>
              {/* Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ backgroundColor: '#dcfce7', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{checkStatusResult.summary.valid}</div>
                  <div style={{ fontSize: '12px', color: '#15803d' }}>✅ Hợp lệ</div>
                </div>
                <div style={{ backgroundColor: '#fef9c3', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#ca8a04' }}>{checkStatusResult.summary.invalid}</div>
                  <div style={{ fontSize: '12px', color: '#a16207' }}>⚠️ Chưa đạt</div>
                </div>
                <div style={{ backgroundColor: '#fee2e2', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{checkStatusResult.summary.errors + checkStatusResult.summary.notFound}</div>
                  <div style={{ fontSize: '12px', color: '#b91c1c' }}>❌ Lỗi</div>
                </div>
              </div>

              {checkStatusResult.updatedToInprogress > 0 && (
                <div style={{ backgroundColor: '#dbeafe', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                  <span style={{ color: '#1d4ed8', fontWeight: '500' }}>
                    ✅ Đã cập nhật {checkStatusResult.updatedToInprogress} đơn về trạng thái "inprogress"
                  </span>
                </div>
              )}

              {/* Valid orders - grouped by status */}
              {checkStatusResult.valid.length > 0 && (() => {
                // Provider status mapping (supports New97 numeric and Sabo text statuses)
                const providerStatusMap: Record<string, string> = {
                  // New97 statuses
                  '0': 'Đang chạy',
                  '1': 'Đã hoàn thành',
                  '3': 'Đang xử lý',
                  '4': 'Đã hủy',
                  '7': 'Đã hoàn tiền',
                  // Sabo statuses
                  'RUNNING': 'Đang chạy',
                  'COMPLETED': 'Đã hoàn thành',
                  'REFUND': 'Đã hoàn tiền',
                  'CANCEL': 'Đã hủy'
                };
                const getStatusLabel = (status: string) => providerStatusMap[status] || providerStatusMap[status.toUpperCase()] || status;

                // Group by status
                const grouped = checkStatusResult.valid.reduce((acc, o) => {
                  const statusLabel = `Status: ${o.status} (${getStatusLabel(o.status)})${o.refund > 0 ? `, Refund: ${o.refund}` : ''}`;
                  if (!acc[statusLabel]) acc[statusLabel] = [];
                  acc[statusLabel].push(o.idSmm);
                  return acc;
                }, {} as Record<string, number[]>);

                return (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ color: '#16a34a', marginBottom: '8px' }}>✅ Đơn hàng hợp lệ ({checkStatusResult.valid.length})</h4>
                    <div style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '12px' }}>
                      {Object.entries(grouped).map(([key, ids], i) => (
                        <div key={i} style={{ marginBottom: i < Object.keys(grouped).length - 1 ? '8px' : 0 }}>
                          <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '500', marginBottom: '4px' }}>
                            {key} ({ids.length} đơn)
                          </div>
                          <div style={{ fontSize: '12px', color: '#166534', wordBreak: 'break-word' }}>
                            #{ids.join(',')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Invalid orders - grouped by status */}
              {checkStatusResult.invalid.length > 0 && (() => {
                // Provider status mapping (supports New97 numeric and Sabo text statuses)
                const providerStatusMap: Record<string, string> = {
                  // New97 statuses
                  '0': 'Đang chạy',
                  '1': 'Đã hoàn thành',
                  '3': 'Đang xử lý',
                  '4': 'Đã hủy',
                  '7': 'Đã hoàn tiền',
                  // Sabo statuses
                  'RUNNING': 'Đang chạy',
                  'COMPLETED': 'Đã hoàn thành',
                  'REFUND': 'Đã hoàn tiền',
                  'CANCEL': 'Đã hủy'
                };
                const getStatusLabel = (status: string) => providerStatusMap[status] || providerStatusMap[status.toUpperCase()] || status;

                // Group by status + DB status
                const grouped = checkStatusResult.invalid.reduce((acc, o) => {
                  const key = `Status: ${o.status} (${getStatusLabel(o.status)}), DB: ${o.currentDbStatus}`;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(o.idSmm);
                  return acc;
                }, {} as Record<string, number[]>);

                return (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ color: '#ca8a04', marginBottom: '8px' }}>⚠️ Đơn chưa đạt trạng thái mong muốn ({checkStatusResult.invalid.length})</h4>
                    <div style={{ backgroundColor: '#fefce8', borderRadius: '8px', padding: '12px' }}>
                      {Object.entries(grouped).map(([key, ids], i) => (
                        <div key={i} style={{ marginBottom: i < Object.keys(grouped).length - 1 ? '8px' : 0 }}>
                          <div style={{ fontSize: '12px', color: '#a16207', fontWeight: '500', marginBottom: '4px' }}>
                            {key} ({ids.length} đơn)
                          </div>
                          <div style={{ fontSize: '12px', color: '#854d0e', wordBreak: 'break-word' }}>
                            #{ids.join(',')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Errors */}
              {(checkStatusResult.errors.length > 0 || checkStatusResult.notFound.length > 0) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ color: '#dc2626', marginBottom: '8px' }}>❌ Lỗi ({checkStatusResult.errors.length + checkStatusResult.notFound.length})</h4>
                  <div style={{ maxHeight: '100px', overflowY: 'auto', backgroundColor: '#fef2f2', borderRadius: '8px', padding: '8px' }}>
                    {checkStatusResult.errors.map((e, i) => (
                      <div key={i} style={{ fontSize: '12px', padding: '4px 8px', color: '#b91c1c' }}>
                        #{e.idSmm}: {e.error}
                      </div>
                    ))}
                    {checkStatusResult.notFound.map((id, i) => (
                      <div key={`nf-${i}`} style={{ fontSize: '12px', padding: '4px 8px', color: '#b91c1c' }}>
                        #{id}: Không tìm thấy trong database
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <button
                  onClick={resetCheckStatusModal}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Đóng
                </button>
              </div>
            </div>
          ) : (
            // Show form
            <>
              <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>
                Kiểm tra trạng thái {selectedOrders.size} đơn hàng đã chọn từ provider.
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  Trạng thái mong muốn:
                </label>
                <select
                  value={checkStatusDesired}
                  onChange={(e) => {
                    setCheckStatusDesired(e.target.value as 'completed' | 'refunded');
                    if (e.target.value !== 'refunded') {
                      setCheckStatusUpdateDb(false);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="completed">✅ Hoàn thành (status = 1)</option>
                  <option value="refunded">💰 Hoàn tiền (status = 7 hoặc status = 0 & refund {'>'} 0)</option>
                </select>
              </div>

              {checkStatusDesired === 'refunded' && (
                <div style={{
                  backgroundColor: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={checkStatusUpdateDb}
                      onChange={(e) => setCheckStatusUpdateDb(e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ color: '#1e40af', fontSize: '13px' }}>
                      Cập nhật đơn <strong>đã hoàn tiền</strong> về trạng thái "inprogress" để hệ thống tự scan lại
                    </span>
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={resetCheckStatusModal}
                  disabled={checkStatusLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontWeight: '500',
                    cursor: checkStatusLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Hủy
                </button>
                <button
                  onClick={handleCheckProviderStatus}
                  disabled={checkStatusLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#0891b2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '500',
                    cursor: checkStatusLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {checkStatusLoading ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Đang kiểm tra...
                    </>
                  ) : (
                    <>🔍 Kiểm tra</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Progress Order Modal */}
      <Modal
        isOpen={progressModal.open}
        onClose={() => setProgressModal({ open: false, idSmm: '', data: [], loading: false, page: 1, limit: 20, total: 0, totalPages: 0 })}
        title={`📊 Progress Order - Đơn #${progressModal.idSmm}`}
        size="xl"
      >
        <div>

            {progressModal.loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                <p style={{ color: '#64748b', margin: 0 }}>Đang tải...</p>
              </div>
            ) : progressModal.data.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>Chưa có dữ liệu progress</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>ID</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>ID API</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Quantity</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Start</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Run</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Count Sent</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Status</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progressModal.data.map((p, idx) => (
                      <tr key={p.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0' }}>{p.id}</td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.idApi || '-'}</td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>{p.quantity.toLocaleString()}</td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>{p.start?.toLocaleString() || '-'}</td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>{p.run.toLocaleString()}</td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>{p.countSent.toLocaleString()}</td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor:
                              p.status === 'completed' ? '#dcfce7' :
                              p.status === 'inprogress' ? '#dbeafe' :
                              p.status === 'pending' ? '#fef3c7' :
                              p.status === 'refund' ? '#fee2e2' :
                              p.status === 'pause' ? '#f3e8ff' : '#f1f5f9',
                            color:
                              p.status === 'completed' ? '#166534' :
                              p.status === 'inprogress' ? '#1e40af' :
                              p.status === 'pending' ? '#92400e' :
                              p.status === 'refund' ? '#991b1b' :
                              p.status === 'pause' ? '#7c3aed' : '#475569'
                          }}>
                            {p.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                          {new Date(p.created).toLocaleString('vi-VN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {progressModal.totalPages > 1 && (
              <div style={{
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderTop: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  Hiển thị {((progressModal.page - 1) * progressModal.limit) + 1} - {Math.min(progressModal.page * progressModal.limit, progressModal.total)} / {progressModal.total} bản ghi
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => fetchProgressOrders(progressModal.idSmm, progressModal.page - 1)}
                    disabled={progressModal.page <= 1 || progressModal.loading}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: progressModal.page <= 1 ? '#f1f5f9' : '#3b82f6',
                      color: progressModal.page <= 1 ? '#94a3b8' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: progressModal.page <= 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Trước
                  </button>
                  <span style={{ fontSize: '13px', color: '#475569', padding: '0 8px' }}>
                    Trang {progressModal.page} / {progressModal.totalPages}
                  </span>
                  <button
                    onClick={() => fetchProgressOrders(progressModal.idSmm, progressModal.page + 1)}
                    disabled={progressModal.page >= progressModal.totalPages || progressModal.loading}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: progressModal.page >= progressModal.totalPages ? '#f1f5f9' : '#3b82f6',
                      color: progressModal.page >= progressModal.totalPages ? '#94a3b8' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: progressModal.page >= progressModal.totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Sau →
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button
                onClick={() => setProgressModal({ open: false, idSmm: '', data: [], loading: false, page: 1, limit: 20, total: 0, totalPages: 0 })}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Đóng
              </button>
            </div>
          </div>
      </Modal>
    </Layout>
  );
};

export default Orders;
